import {
  TABLE_CELL_MAX_LENGTH,
  TABLE_CELLS_MAX_JSON_BYTES,
  TABLE_CELLS_MAX_TOTAL_CHARS,
  TABLE_CELL_PADDING_EM,
  TABLE_MIN_COLUMN_EM,
  TABLE_ROW_MIN_HEIGHT_EM,
  estimateEmWidth,
  fitTableCellsForBudget,
  normalizeCaretToCodePointBoundary,
} from "@refinement-canvas/domain";

/** 貼り付け直後の表がキャンバスを覆い尽くさない最大幅。 */
export const TABLE_PASTE_MAX_W = 960;
/** 貼り付け直後の表がキャンバスを覆い尽くさない最大高さ。 */
export const TABLE_PASTE_MAX_H = 720;

/**
 * 貼り付ける表の初期サイズ（ステージ座標 px）を、列数・行数とセル本文から決める。
 * 幅は各列の自然幅、高さは行の最低高を使い、それぞれ貼り付け用上限へ収める。
 */
export function resolvePastedTableFrameSize(params: {
  readonly cells: readonly (readonly string[])[];
  readonly fontPx: number;
}): { readonly w: number; readonly h: number } {
  const fontPx = Number.isFinite(params.fontPx) && params.fontPx > 0 ? params.fontPx : 16;
  const rowCount = Math.max(1, params.cells.length);
  const columnCount = Math.max(1, params.cells[0]?.length ?? 0);
  const naturalWidthsEm = new Array<number>(columnCount).fill(TABLE_MIN_COLUMN_EM);
  for (const row of params.cells) {
    for (let column = 0; column < columnCount; column += 1) {
      naturalWidthsEm[column] = Math.max(
        naturalWidthsEm[column]!,
        estimateEmWidth(row[column] ?? "") + TABLE_CELL_PADDING_EM
      );
    }
  }
  // 列ごとの自然幅を合計し、表全体の初期幅へ変換する。
  const naturalWidthEm = naturalWidthsEm.reduce((sum, width) => sum + width, 0);
  return {
    w: Math.min(TABLE_PASTE_MAX_W, naturalWidthEm * fontPx),
    h: Math.min(TABLE_PASTE_MAX_H, rowCount * TABLE_ROW_MIN_HEIGHT_EM * fontPx),
  };
}

export interface FittedPastedTableCells {
  readonly cells: readonly (readonly string[])[];
  readonly truncated: boolean;
}

/** セル単体・合計文字数・JSON バイト数の順に、保存契約へ収まるよう末尾側を切り詰める。 */
export function fitPastedTableCells(
  source: readonly (readonly string[])[]
): FittedPastedTableCells | null {
  let remainingChars = TABLE_CELLS_MAX_TOTAL_CHARS;
  let truncated = false;
  const cells = source.map((row) =>
    row.map((cell) => {
      const cellLimit = Math.min(TABLE_CELL_MAX_LENGTH, remainingChars);
      // UTF-16 の上限位置がサロゲートペアの途中なら、直前のコードポイント境界へ戻す。
      const fitted = cell.slice(0, normalizeCaretToCodePointBoundary(cell, cellLimit));
      if (fitted.length !== cell.length) truncated = true;
      remainingChars -= fitted.length;
      return fitted;
    })
  );
  const budgetFit = fitTableCellsForBudget({
    cells,
    budgetBytes: TABLE_CELLS_MAX_JSON_BYTES,
  });
  if (budgetFit === null) return null;
  return { cells: budgetFit.cells, truncated: truncated || budgetFit.clamped };
}
